import { Fragment, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { ArrowLeft, ArrowRight, ChevronsUpDown, Check, Loader2 } from "lucide-react"
import HomeNav from "@/components/home-nav"
import HomeFooter from "@/components/home-footer"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { api, ApiError, setToken, setUserProfile, setOrganizationsProfile, orgApi } from "@/lib/api"
import type { UserProfile, OrganisationProfile } from "@/lib/types"

const TECHNOLOGY_TYPES = [
  "Advanced Conversion Technologies",
  "Anaerobic Digestion",
  "Battery",
  "Biomass (co-firing)",
  "Biomass (dedicated)",
  "Compressed Air Energy Storage",
  "EfW Incineration",
  "Flywheels",
  "Fuel Cell (Hydrogen)",
  "Geothermal",
  "Hot Dry Rocks (HDR)",
  "Hydrogen",
  "Landfill Gas",
  "Large Hydro",
  "Liquid Air Energy Storage",
  "Pumped Storage Hydroelectricity",
  "Sewage Sludge Digestion",
  "Shoreline Wave",
  "Small Hydro",
  "Solar Photovoltaics",
  "Tidal Lagoon",
  "Tidal Stream",
  "Wind Offshore",
  "Wind Onshore",
]

type PageState = "checking" | "invalid" | "form" | "success"
type FormStep = "account" | "preferences"

export default function AcceptInvitePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [pageState, setPageState] = useState<PageState>("checking")
  const [errorMessage, setErrorMessage] = useState("")
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteOrg, setInviteOrg] = useState("")
  const [formStep, setFormStep] = useState<FormStep>("account")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [techOpen, setTechOpen] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [apiError, setApiError] = useState("")
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    preferences: [] as string[],
  })

  useEffect(() => {
    const token = searchParams.get("token")
    if (!token) {
      setErrorMessage("No invite token found. Please use the link from your invitation email.")
      setPageState("invalid")
      return
    }

    api
      .get<{ data: { email: string; organization: string } }>(`/auth/check-invite?token=${token}`)
      .then((res) => {
        setInviteEmail(res.data.email)
        setInviteOrg(res.data.organization)
        setPageState("form")
      })
      .catch((err) => {
        const msg = err instanceof ApiError ? err.message : ""
        const lower = msg.toLowerCase()
        if (lower.includes("expir")) {
          setErrorMessage("This invite link has expired. Please ask your administrator to send a new invitation.")
        } else {
          setErrorMessage("This invite link is invalid. Please make sure you used the correct link from your email.")
        }
        setPageState("invalid")
      })
  }, [searchParams])

  const validateAccountStep = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required"
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required"
    if (!formData.password) {
      newErrors.password = "Password is required"
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters"
    } else if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = "Password must contain letters and numbers"
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password"
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"
    }
    return newErrors
  }

  const validatePreferencesStep = () => {
    const newErrors: Record<string, string> = {}
    if (formData.preferences.length === 0) newErrors.preferences = "Please select at least one project type"
    return newErrors
  }

  const handleNext = async () => {
    if (formStep === "account") {
      const newErrors = validateAccountStep()
      setErrors(newErrors)
      if (Object.keys(newErrors).length > 0) return
      setFormStep("preferences")
      return
    }

    const newErrors = validatePreferencesStep()
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    const token = searchParams.get("token")!
    setIsLoading(true)
    setApiError("")
    try {
      const res = await api.post<{ data: { token: string; safeUser: UserProfile } }>("/auth/accept-invite", {
        token,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: inviteEmail,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        preferences: formData.preferences,
      })
      if (res.data?.token) {
        setToken(res.data.token)
        try {
          const jwtPayload = JSON.parse(atob(res.data.token.split(".")[1]))
          const jwtUser = jwtPayload.user as { role: string; organization_id: string }
          const profile: UserProfile = { ...res.data.safeUser, role: jwtUser.role, organization_id: jwtUser.organization_id }
          setUserProfile(profile)
          if (jwtUser.organization_id) {
            const orgRes = await orgApi.getOrganisation(jwtUser.organization_id)
            setOrganizationsProfile([orgRes.data as OrganisationProfile])
          }
        } catch(err) {
          console.error("Failed to parse JWT or fetch user/org profile:", err)
        }
      }
      setPageState("success")
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : "Failed to accept invite. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    setErrors({})
    setFormStep("account")
  }

  const steps: FormStep[] = ["account", "preferences"]
  const stepLabels = ["Account", "Preferences"]

  return (
    <>
      <HomeNav />

      <div className="flex min-h-[calc(100svh-4rem)]">
        <div className="flex-1 flex items-center justify-center bg-white px-6 py-16 sm:px-12 overflow-y-auto">
          <div className="w-full max-w-md">

            {/* Checking */}
            {pageState === "checking" && (
              <div>
                <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-3">
                  {"Checking your invite"}
                </h1>
                <p className="text-[#0D0D0D]/55 text-base leading-relaxed mb-10">
                  {"Please wait while we validate your invitation link."}
                </p>
                <div className="border border-[#0D0D0D]/10 p-8 flex items-center gap-5">
                  <Loader2 className="h-5 w-5 animate-spin text-secondary shrink-0" />
                  <span className="text-sm text-[#0D0D0D]/65">{"Validating your token…"}</span>
                </div>
              </div>
            )}

            {/* Invalid */}
            {pageState === "invalid" && (
              <div>
                <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-3">
                  {"Invite link invalid"}
                </h1>
                <p className="text-[#0D0D0D]/55 text-base leading-relaxed mb-10">
                  {errorMessage}
                </p>
                <div className="border border-[#0D0D0D]/10 p-8 mb-8">
                  <h4 className="font-bold text-[#0D0D0D] mb-4 text-xs uppercase tracking-widest">
                    {"What you can do"}
                  </h4>
                  <ul className="space-y-3">
                    {[
                      "Make sure you used the most recent invitation link",
                      "Invite links expire after a limited time",
                      "Contact your administrator to resend the invitation",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 shrink-0" />
                        <span className="text-sm text-[#0D0D0D]/65 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  className="w-full rounded-none bg-[#0D0D0D] hover:bg-[#1D591B] text-white h-12 font-semibold text-sm tracking-wide transition-colors duration-200 cursor-pointer"
                  onClick={() => navigate("/register")}
                >
                  {"Back to Onboarding"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Form */}
            {pageState === "form" && (
              <>
                {/* Progress */}
                <div className="mb-14">
                  <div className="flex items-center">
                    {stepLabels.map((label, index) => {
                      const currentIndex = steps.indexOf(formStep)
                      const isActive = index === currentIndex
                      const isComplete = index < currentIndex
                      return (
                        <Fragment key={label}>
                          <div className="flex flex-col items-center gap-2">
                            <div
                              className={`w-9 h-9 flex items-center justify-center text-sm font-bold transition-colors duration-200 ${
                                isActive
                                  ? "bg-[#0D0D0D] text-white"
                                  : isComplete
                                    ? "bg-[#1D591B] text-white"
                                    : "bg-[#0D0D0D]/8 text-[#0D0D0D]/35"
                              }`}
                            >
                              {index + 1}
                            </div>
                            <span
                              className={`text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap transition-colors duration-200 ${
                                isActive ? "text-[#0D0D0D]" : isComplete ? "text-[#1D591B]" : "text-[#0D0D0D]/30"
                              }`}
                            >
                              {label}
                            </span>
                          </div>
                          {index < stepLabels.length - 1 && (
                            <div
                              className={`flex-1 h-px mx-4 mb-5 transition-colors duration-200 ${
                                isComplete ? "bg-[#1D591B]" : "bg-[#0D0D0D]/10"
                              }`}
                            />
                          )}
                        </Fragment>
                      )
                    })}
                  </div>
                </div>

                {/* Account Step */}
                {formStep === "account" && (
                  <div>
                    <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                    <h2 className="text-3xl font-black text-[#0D0D0D] tracking-tight mb-2">{"Set up your account"}</h2>
                    <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-2">
                      {"You've been invited to join "}
                      <span className="font-semibold text-[#0D0D0D]/80">{inviteOrg}</span>
                      {"."}
                    </p>
                    <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
                      {"Complete your profile to get started."}
                    </p>
                    <div className="space-y-5">
                      <div className="space-y-1.5">
                        <Label htmlFor="firstName" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                          {"First Name"} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="firstName" placeholder="John" value={formData.firstName}
                          onChange={(e) => { setFormData({ ...formData, firstName: e.target.value }); if (errors.firstName) setErrors({ ...errors, firstName: "" }) }}
                          className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0"
                          aria-invalid={!!errors.firstName}
                        />
                        {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="lastName" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                          {"Last Name"} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="lastName" placeholder="Smith" value={formData.lastName}
                          onChange={(e) => { setFormData({ ...formData, lastName: e.target.value }); if (errors.lastName) setErrors({ ...errors, lastName: "" }) }}
                          className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0"
                          aria-invalid={!!errors.lastName}
                        />
                        {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                          {"Work Email"}
                        </Label>
                        <Input
                          id="email" type="email" value={inviteEmail} disabled
                          className="rounded-none h-11 border-[#0D0D0D]/20 bg-[#0D0D0D]/4 text-[#0D0D0D]/50 cursor-not-allowed"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                          {"Password"} <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={formData.password} autoComplete="new-password"
                            onChange={(e) => { setFormData({ ...formData, password: e.target.value }); if (errors.password) setErrors({ ...errors, password: "" }) }}
                            className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 pr-10"
                            aria-invalid={!!errors.password}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D0D0D]/35 hover:text-[#0D0D0D]/60 transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            <span className="text-xs font-medium">{showPassword ? "Hide" : "Show"}</span>
                          </button>
                        </div>
                        {errors.password ? (
                          <p className="text-xs text-destructive">{errors.password}</p>
                        ) : (
                          <p className="text-xs text-[#0D0D0D]/40">{"Must be at least 8 characters with a mix of letters and numbers"}</p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="confirmPassword" className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                          {"Confirm Password"} <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="confirmPassword" type={showConfirmPassword ? "text" : "password"} placeholder="••••••••" value={formData.confirmPassword} autoComplete="new-password"
                            onChange={(e) => { setFormData({ ...formData, confirmPassword: e.target.value }); if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: "" }) }}
                            className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 pr-10"
                            aria-invalid={!!errors.confirmPassword}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D0D0D]/35 hover:text-[#0D0D0D]/60 transition-colors"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            <span className="text-xs font-medium">{showConfirmPassword ? "Hide" : "Show"}</span>
                          </button>
                        </div>
                        {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
                      </div>
                      <div className="pt-4">
                        <Button
                          className="w-full rounded-none bg-[#0D0D0D] hover:bg-[#1D591B] text-white h-11 font-semibold transition-colors duration-200 cursor-pointer"
                          onClick={handleNext}
                        >
                          {"Continue"} <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preferences Step */}
                {formStep === "preferences" && (
                  <div>
                    <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                    <h2 className="text-3xl font-black text-[#0D0D0D] tracking-tight mb-2">{"Customize your experience"}</h2>
                    <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
                      {"Select the types of projects you're most interested in tracking."}
                    </p>
                    <div className="space-y-8">
                      <div className="space-y-3">
                        <Label className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60">
                          {"Project Types of Interest"} <span className="text-destructive">*</span>
                        </Label>
                        <Popover open={techOpen} onOpenChange={setTechOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={techOpen}
                              className={`w-full min-h-11 h-auto rounded-none border-[#0D0D0D]/20 bg-transparent hover:bg-transparent justify-between font-normal text-sm px-3 py-2.5 ${techOpen ? "border-[#1D591B]" : ""} ${formData.preferences.length === 0 ? "text-[#0D0D0D]/40" : "text-[#0D0D0D]"}`}
                            >
                              <span className="flex flex-wrap gap-1.5">
                                {formData.preferences.length === 0
                                  ? "Select technology types"
                                  : formData.preferences.map((t) => (
                                      <span key={t} className="inline-flex items-center bg-[#1D591B]/10 text-[#1D591B] text-xs px-2 py-0.5 font-medium">
                                        {t}
                                      </span>
                                    ))}
                              </span>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-(--radix-popover-trigger-width) p-0 rounded-none" align="start">
                            <Command>
                              <CommandInput placeholder="Search technology..." className="h-9" />
                              <CommandList className="max-h-60">
                                <CommandEmpty>{"No technology found."}</CommandEmpty>
                                <CommandGroup>
                                  {TECHNOLOGY_TYPES.map((tech) => (
                                    <CommandItem
                                      key={tech}
                                      value={tech}
                                      onSelect={() => {
                                        const next = formData.preferences.includes(tech)
                                          ? formData.preferences.filter((i) => i !== tech)
                                          : [...formData.preferences, tech]
                                        setFormData({ ...formData, preferences: next })
                                        if (errors.preferences) setErrors({ ...errors, preferences: "" })
                                      }}
                                    >
                                      <Checkbox
                                        checked={formData.preferences.includes(tech)}
                                        className="mr-2 pointer-events-none"
                                      />
                                      {tech}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {errors.preferences && <p className="text-xs text-destructive">{errors.preferences}</p>}
                      </div>
                      {apiError && <p className="text-sm text-destructive">{apiError}</p>}
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          className="flex-1 rounded-none border-[#0D0D0D]/20 bg-transparent text-[#0D0D0D] hover:border-[#0D0D0D] hover:bg-transparent hover:text-primary h-11"
                          onClick={handleBack} disabled={isLoading}
                        >
                          <ArrowLeft className="mr-2 h-4 w-4" /> {"Back"}
                        </Button>
                        <Button
                          className="flex-1 rounded-none bg-[#0D0D0D] hover:bg-[#1D591B] text-white h-11 font-semibold transition-colors duration-200 cursor-pointer"
                          onClick={handleNext} disabled={isLoading}
                        >
                          {isLoading ? "Accepting invite…" : "Accept Invite"}
                          {!isLoading && <ArrowRight className="ml-2 h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Success */}
            {pageState === "success" && (
              <div>
                <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
                <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-3">
                  {"You're in!"}
                </h1>
                <p className="text-[#0D0D0D]/55 text-base leading-relaxed mb-10">
                  {"Your account has been set up and you've joined "}
                  <span className="font-semibold text-[#0D0D0D]/80">{inviteOrg}</span>
                  {". Start exploring the platform."}
                </p>
                <div className="border border-[#0D0D0D]/10 p-8 mb-8">
                  <h4 className="font-bold text-[#0D0D0D] mb-5 text-xs uppercase tracking-widest">{"What's next?"}</h4>
                  <ul className="space-y-4">
                    {[
                      "Explore the AI-powered dashboard and ask your first question",
                      "Browse projects by region, technology, and Gate 2 likelihood",
                      "Collaborate with your team on analysis",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 shrink-0" />
                        <span className="text-sm text-[#0D0D0D]/65 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  className="w-full rounded-none bg-[#0D0D0D] hover:bg-[#1D591B] text-white h-12 font-semibold text-sm tracking-wide transition-colors duration-200 cursor-pointer"
                  onClick={() => navigate("/dashboard")}
                >
                  {"Go to Dashboard"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

          </div>
        </div>

        {/* Right — Brand panel */}
        <div className="hidden lg:flex w-[48%] bg-[url('/grid.jpg')] bg-cover bg-center bg-no-repeat relative overflow-hidden" />
      </div>

      <HomeFooter />
    </>
  )
}
